import { useRef, type FC } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useFragment } from 'react-relay';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile.tsx';
import { BarHeader } from 'dibs-elements/exports/BarHeader.tsx';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { QuickViewProvider } from 'dibs-buyer-product-tile/exports/QuickView.ts';
import { useSbSharedItemTracking } from '../hooks/sharedItemTracking/useSbSharedItemTracking.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

import { type SbSharedRecentlySoldItems_itemSearch$key } from './__generated__/SbSharedRecentlySoldItems_itemSearch.graphql.ts';
import { type SbSharedRecentlySoldItems_viewer$key } from './__generated__/SbSharedRecentlySoldItems_viewer.graphql.ts';

import styles from './SbSharedRecentlySoldItems.scss';

type Props = {
    viewer: SbSharedRecentlySoldItems_viewer$key;
    itemSearch: SbSharedRecentlySoldItems_itemSearch$key;
};

export const SbSharedRecentlySoldItems: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
}) => {
    const gridContainerRef = useRef<HTMLDivElement | null>(null);
    const viewer = useFragment(
        graphql`
            fragment SbSharedRecentlySoldItems_viewer on Viewer {
                ...SharedItemTile_viewer
                ...useSbSharedItemTracking_viewer
            }
        `,
        viewerRef
    );

    const itemSearch = useFragment(
        graphql`
            fragment SbSharedRecentlySoldItems_itemSearch on ItemSearchQueryConnection {
                ...SharedItemTile_itemSearch
                ...useSbSharedItemTracking_itemSearch
                soldResults {
                    edges {
                        node {
                            item {
                                serviceId
                                ...useSbSharedItemTracking_item
                                ...SharedItemTile_item
                                    @arguments(
                                        isTrade: $isTrade
                                        priceBookName: $priceBookName
                                        showSeller: $showSeller
                                    )
                            }
                        }
                    }
                }
            }
        `,
        itemSearchRef
    );

    const thinBrowseSoldItems = (itemSearch?.soldResults?.edges || [])
        .map(item => item?.node?.item || null)
        .filter(filterFalsy);

    const { isQuickFiltersRedesignVariant } = useServerVarsContext();
    const { fireItemImpressionTracking, fireItemClickTracking } = useSbSharedItemTracking({
        viewer,
        itemSearch,
        items: thinBrowseSoldItems,
        pageType: 'previously available',
    });

    if (!thinBrowseSoldItems.length) {
        return null;
    }

    return (
        <>
            <BarHeader
                title={
                    <FormattedMessage
                        id="sb.SbSharedRecentlySoldItems.title"
                        defaultMessage="Previously Available Items"
                    />
                }
            />
            <QuickViewProvider elementRef={gridContainerRef}>
                <div
                    className={classnames(styles.grid, {
                        [styles.isQuickFiltersRedesignVariant]: isQuickFiltersRedesignVariant,
                    })}
                    data-tn="previously-available-items"
                    ref={gridContainerRef}
                >
                    {thinBrowseSoldItems.map((soldItem, index) => {
                        return (
                            <SharedItemTile
                                user={null}
                                key={soldItem?.serviceId}
                                index={index}
                                item={soldItem}
                                viewer={viewer}
                                itemSearch={itemSearch}
                                onContentVisible={() =>
                                    fireItemImpressionTracking({
                                        itemId: soldItem?.serviceId,
                                        index,
                                    })
                                }
                                onClick={() =>
                                    fireItemClickTracking({
                                        itemId: soldItem?.serviceId,
                                        index,
                                    })
                                }
                                hideRecentSoldPrice
                            />
                        );
                    })}
                </div>
            </QuickViewProvider>
        </>
    );
};
